import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import '../css/auth.css';


export default function Signup() {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  // Master State Form Data
  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    handle: '',
    location: '',
    is_scout: false,
    creative_types: [],
    plan: 'starter',
    billing: 'monthly',
  });

  const [handleStatus, setHandleStatus] = useState('idle'); // 'idle', 'checking', 'available', 'taken'
  const [backendTrialDate, setBackendTrialDate] = useState('');

  // Live Handle Validation (Debounced)
  useEffect(() => {
    const checkHandle = async () => {
      if (formData.handle.length < 3) {
        setHandleStatus('idle');
        return;
      }
      
      setHandleStatus('checking');
      try {
        const res = await api.get(`/auth/check-handle?handle=${formData.handle}`);
        setHandleStatus(res.data.available ? 'available' : 'taken');
      } catch (error) {
        console.error('Failed to check handle:', error);
        setHandleStatus('idle');
      }
    };

    const timeoutId = setTimeout(checkHandle, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.handle]);

  const toggleCreativeType = (type) => {
    if (type === 'Scout') {
      const isSelectingScout = !formData.is_scout;
      setFormData({
        ...formData,
        is_scout: isSelectingScout,
        creative_types: isSelectingScout ? ['Scout'] : [],
      });
    } else {
      let updated = [...formData.creative_types].filter((t) => t !== 'Scout');
      if (updated.includes(type)) {
        updated = updated.filter((t) => t !== type);
      } else {
        updated.push(type);
      }
      setFormData({
        ...formData,
        is_scout: false,
        creative_types: updated,
      });
    }
  };

  // Submit complete signup payload & log user in
  const handleFinalSignup = async () => {
    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        handle: formData.handle,
        location: formData.location,
        is_scout: formData.is_scout,
        creative_types: formData.creative_types,
        plan: formData.plan,
        billing: formData.billing,
      };

      const response = await api.post('/auth/register', payload);
      const { token, user } = response.data;

      // Save session details locally
      if (token) {
        localStorage.setItem('token', token);
        if (authContext && authContext.login) {
          authContext.login(token, user);
        }
      }

      if (user?.ai_trial_ends_at) {
        const realDate = new Date(user.ai_trial_ends_at);
        setBackendTrialDate(realDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }));
      }

      setStep(7);
    } catch (err) {
      alert('Signup failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="shell">

        <div className="screens">
          {/* ================= S0: WELCOME SCREEN ================= */}
          {step === 0 && (
            <div className="screen active">
              <div className="bg-art">
                <div className="bg-orb" style={{ width: '280px', height: '280px', background: 'var(--glow)', top: '-80px', right: '-80px' }}></div>
                <div className="bg-orb" style={{ width: '200px', height: '200px', background: 'var(--mint)', bottom: '-60px', left: '-60px' }}></div>
              </div>

              <div className="welcome-art">
                <div className="welcome-globe">
                  <svg viewBox="0 0 180 180" width="180" height="180">
                    <circle cx="90" cy="90" r="72" fill="#7B6EF0" opacity="0.3" />
                    <circle cx="90" cy="90" r="50" fill="#1EDBA8" opacity="0.5" />
                  </svg>
                </div>

                <div className="creative-pills">
                  <span className="cpill cpill-0">Designers</span>
                  <span className="cpill cpill-1">Musicians</span>
                  <span className="cpill cpill-2">Photographers</span>
                  <span className="cpill cpill-3">Scouts</span>
                  <span className="cpill cpill-0">Creators</span>
                </div>

                <div className="welcome-text">
                  <div className="welcome-title">Where the world<br />meets <em>creative</em><br />talent.</div>
                  <div className="welcome-sub">Buniverse is for everyone — discover, connect, and collaborate with talent across every field.</div>
                </div>

                <button className="btn-primary" onClick={() => setStep(1)} style={{ marginBottom: '16px' }}>
                  Create your profile — it's free
                </button>

                <div className="terms-note">
                  By continuing you agree to our <a href="/terms">Terms</a> and <a href="/privacy">Privacy Policy</a>
                </div>
              </div>
            </div>
          )}

          {/* ================= S1: EMAIL & PASSWORD ================= */}
          {step === 1 && (
            <div className="screen active">
              <div className="progress-wrap">
                <div className="progress-bar"><div className="progress-fill" style={{ width: '20%' }}></div></div>
                <div className="progress-meta">
                  <button className="back-btn" onClick={() => setStep(0)}>← Back</button>
                  <span className="step-text">Step 1 of 5</span>
                </div>
              </div>

              <div className="scroll-body pt-24">
                <div className="display">Let's get<br /><em>started</em></div>
                <div className="lead">Your email and a secure password. That's all we need to begin.</div>

                <div className="field">
                  <span className="label">Email address</span>
                  <input
                    className="input"
                    type="email"
                    placeholder="you@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div className="field">
                  <span className="label">Password</span>
                  <div className="input-wrap">
                    <input
                      className="input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                    <div className="input-icon" onClick={() => setShowPassword(!showPassword)}>👁</div>
                  </div>
                </div>

                <div className="mt-24">
                  <button
                    className="btn-primary"
                    disabled={!formData.email.includes('@') || formData.password.length < 8}
                    onClick={() => setStep(3)} /* Bypasses OTP directly to Step 3 */
                  >
                    Continue
                  </button>
                </div>

                <div className="terms-note mt-16">
                  Already have an account? <Link to="/login" style={{ color: 'var(--glow)' }}>Log in</Link>
                </div>
              </div>
            </div>
          )}

          {/* ================= S3: NAME & HANDLE ================= */}
          {step === 3 && (
            <div className="screen active">
              <div className="progress-wrap">
                <div className="progress-bar"><div className="progress-fill" style={{ width: '40%' }}></div></div>
                <div className="progress-meta">
                  <button className="back-btn" onClick={() => setStep(1)}>← Back</button>
                  <span className="step-text">Step 2 of 5</span>
                </div>
              </div>

              <div className="scroll-body pt-24">
                <div className="display">Who are<br /><em>you?</em></div>
                <div className="lead">Your name, a unique handle, and your location.</div>

                <div className="field">
                  <span className="label">Full name</span>
                  <input
                    className="input"
                    type="text"
                    placeholder="e.g. Amara Osei"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div className="field">
                  <span className="label">Your @handle</span>
                  <input
                    className={`input ${handleStatus === 'taken' ? 'input-error' : ''}`}
                    type="text"
                    placeholder="amara.creates"
                    value={formData.handle}
                    onChange={(e) => setFormData({ ...formData, handle: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/gi, '') })}
                  />
                  {formData.handle.length >= 3 && (
                    <div className="handle-preview show" style={{ marginTop: '8px', fontSize: '13px' }}>
                      {handleStatus === 'checking' && <span style={{ color: 'var(--subtext)' }}>Checking availability...</span>}
                      {handleStatus === 'available' && <span style={{ color: 'var(--mint)' }}>✓ Handle available! (buniverse.io/@{formData.handle})</span>}
                      {handleStatus === 'taken' && <span style={{ color: '#FF5C5C' }}>✕ Handle is already taken</span>}
                    </div>
                  )}
                </div>            

                <div className="field">
                  <span className="label">Location</span>
                  <input
                    className="input"
                    type="text"
                    placeholder="e.g. Nairobi, Kenya"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  />
                </div>

                <button
                  className="btn-primary mt-16"
                  disabled={!formData.name || !formData.handle || !formData.location || handleStatus === 'taken' || handleStatus === 'checking'}
                  onClick={() => setStep(4)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* ================= S4: ACCOUNT / CREATIVE TYPE ================= */}
          {step === 4 && (
            <div className="screen active">
              <div className="progress-wrap">
                <div className="progress-bar"><div className="progress-fill" style={{ width: '60%' }}></div></div>
                <div className="progress-meta">
                  <button className="back-btn" onClick={() => setStep(3)}>← Back</button>
                  <span className="step-text">Step 3 of 5</span>
                </div>
              </div>

              <div className="scroll-body pt-24">
                <div className="display">Select your<br /><em>account type</em></div>
                <div className="lead">Are you here to showcase your skills or search for talent?</div>

                <div className="type-grid">
                  <div
                    className={`type-card ${formData.is_scout ? 'selected' : ''}`}
                    onClick={() => toggleCreativeType('Scout')}
                    style={{ gridColumn: '1 / -1', borderColor: 'var(--mint)' }}
                  >
                    <span className="type-emoji">🔍</span>
                    <span className="type-name">Scout / Talent Hunter</span>
                    <span className="type-desc">I am an agent, brand, or recruiter looking to discover creative talent.</span>
                  </div>

                  {[
                    { name: 'Designer', icon: '🎨', desc: 'Brand, UI/UX, illustration' },
                    { name: 'Photographer', icon: '📸', desc: 'Fashion, editorial, portrait' },
                    { name: 'Musician', icon: '🎵', desc: 'Producer, artist, composer' },
                    { name: 'Writer', icon: '✍️', desc: 'Journalist, blogger, poet' },
                    { name: 'Actor', icon: '🎭', desc: 'Film, TV, theatre' },
                    { name: 'Creator', icon: '📱', desc: 'Content, influencer, host' },
                  ].map((item) => (
                    <div
                      key={item.name}
                      className={`type-card ${formData.creative_types.includes(item.name) ? 'selected' : ''}`}
                      onClick={() => toggleCreativeType(item.name)}
                    >
                      <span className="type-emoji">{item.icon}</span>
                      <span className="type-name">{item.name}</span>
                      <span className="type-desc">{item.desc}</span>
                    </div>
                  ))}
                </div>

                <button
                  className="btn-primary mt-16"
                  disabled={!formData.is_scout && formData.creative_types.length === 0}
                  onClick={() => setStep(5)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* ================= S5: AI TRIAL SHOWCASE ================= */}
          {step === 5 && (
            <div className="screen active">
              <div className="progress-wrap">
                <div className="progress-bar"><div className="progress-fill" style={{ width: '80%' }}></div></div>
                <div className="progress-meta">
                  <button className="back-btn" onClick={() => setStep(4)}>← Back</button>
                  <span className="step-text">Step 4 of 5</span>
                </div>
              </div>

              <div className="scroll-body pt-16">
                <div className="ai-trial-hero">
                  <div className="ai-trial-badge">🤖 Exclusive to new accounts</div>
                  <div className="ai-trial-title">Buni AI Studio —<br /><em>7 days free</em></div>
                  <div className="ai-trial-sub">Get a full week of Buni AI Studio included with your account. No card required.</div>
                </div>

                <button className="btn-primary" onClick={() => setStep(6)}>Activate 7-day trial ✦</button>
                <button className="btn-ghost" onClick={() => setStep(6)}>Skip trial</button>
              </div>
            </div>
          )}

          {/* ================= S6: PLAN SELECTION ================= */}
          {step === 6 && (
            <div className="screen active">
              <div className="progress-wrap">
                <div className="progress-bar"><div className="progress-fill" style={{ width: '100%' }}></div></div>
                <div className="progress-meta">
                  <button className="back-btn" onClick={() => setStep(5)}>← Back</button>
                  <span className="step-text">Step 5 of 5</span>
                </div>
              </div>

              <div className="scroll-body pt-24">
                <div className="display">Choose your<br /><em>plan</em></div>

                <div className="plan-pills">
                  <button
                    className={`plan-pill ${formData.billing === 'monthly' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, billing: 'monthly' })}
                  >
                    Monthly
                  </button>
                  <button
                    className={`plan-pill ${formData.billing === 'annual' ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, billing: 'annual' })}
                  >
                    Annual (Save 20%)
                  </button>
                </div>

                <div
                  className={`plan-card ${formData.plan === 'starter' ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, plan: 'starter' })}
                >
                  <div className="plan-top">
                    <div className="plan-name">Starter</div>
                    <div className="plan-price">Free</div>
                  </div>
                </div>

                <div
                  className={`plan-card ${formData.plan === 'mid' ? 'selected' : ''}`}
                  onClick={() => setFormData({ ...formData, plan: 'mid' })}
                >
                  <div className="plan-top">
                    <div className="plan-name">Mid / Pro</div>
                    <div className="plan-price">{formData.billing === 'annual' ? '$7.20/mo' : '$9/mo'}</div>
                  </div>
                </div>

                <button className="btn-primary mt-16" disabled={loading} onClick={handleFinalSignup}>
                  {loading ? 'Creating Account...' : 'Complete Registration'}
                </button>
              </div>
            </div>
          )}

          {/* ================= S7: SUCCESS ================= */}
          {step === 7 && (
            <div className="screen active">
              <div className="success-screen">
                <div className="success-circle">🎉</div>
                <div className="success-title">Welcome to<br /><em>Buniverse</em></div>
                <div className="success-sub">Your profile is live. Explore the creative universe now.</div>

                {backendTrialDate && (
                  <div className="ai-trial-strip">
                    <div>
                      <div className="ats-title">Buni AI Active</div>
                      <div className="ats-sub">Trial valid until <strong>{backendTrialDate}</strong></div>
                    </div>
                  </div>
                )}

                <button className="btn-primary" onClick={() => navigate('/')}>
                  Enter Buniverse →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}